import React, { useEffect, useState } from "react";
import "../styles/LoginPage.css";
import { data, useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import GoogleAuth from "../objects/GoogleAuth";
import MicrosoftAuth from "../objects/MicrosoftAuth";
import { IndexHeaderNav } from "./IndexHeaderNav";
import * as Sentry from '@sentry/react';


function Login() {
    const [recover, setRecover] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [waiting, setWaiting] = useState(false);
    const [fromRecover, setFromRecover] = useState(false);
    const [adminLogin, setAdminLogin] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const { theme } = useTheme();

    const provider = (theme?.provider || "").toLowerCase().trim();
    const showGoogleAuth = provider === "google" || provider === "all" || provider === "any";
    const showMicrosoftAuth = false; // provider === "microsoft" || provider === "all" || provider === "any";


    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant", // or "smooth" if you want
        });
    }, []);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("fromRecover") === "true") {
            setFromRecover(true);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("type") === "admin") {
            setAdminLogin(true);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get("oauthError") === "true") {
            setErrorMessage("Authentication failed. Please try again.");
        }

        if (params.get("googleNotFound") === "true") {
            setErrorMessage("No account found for that email. Please register to continue.");
        }
    }, []);


    useEffect(() => {
        setShowPassword(false);
    }, [recover]);


    function validateLoginForm(event) {
        const inputs = document.querySelectorAll('input[type="email"]');
        const forbiddenKeywords = ["DROP", "DELETE", "INSERT", "UPDATE", "SELECT", "--", ";"];

        for (let input of inputs) {
            const value = input.value.trim();
            const upperValue = value.toUpperCase();

            for (let keyword of forbiddenKeywords) {
                if (upperValue.includes(keyword)) {
                    alert(`Input rejected in "${input.placeholder}". Please try again.`);
                    input.focus();
                    event.preventDefault();
                    return false;
                }
            }
        }

        return true;
    }

    const HandleLogIn = async (event) => {
        event.preventDefault();
        setWaiting(true);

        try {

            const result = await fetch("/api/login", {
                method: "POST",
                body: new FormData(event.target),
            });

            if (result.ok) {
                result.json().then((data) => {
                    if (data.redirect) {
                        setWaiting(false);
                        window.location.href = data.redirect;
                    } else if (data.error_message) {
                        setErrorMessage(data.error_message);
                        setWaiting(false);
                    } else {
                        setWaiting(false);
                    }
                });
            } else {
                const data = await result.json();
                setErrorMessage(data.error_message || "Login failed. Please try again.");
                setWaiting(false);
            }
        } catch (e) {
            setErrorMessage('Network error. Please try again.');
            setWaiting(false);
            Sentry.captureException(e);
        } finally {
            setWaiting(false);
        }
    };

    const HandleAdminLogIn = async (event) => {
        event.preventDefault();
        setWaiting(true);

        try {
            const result = await fetch("/api/admin_login", {
                method: "POST",
                body: new FormData(event.target),
            });

            const data = await result.json();

            if (!result.ok) {
                setErrorMessage(data.error_message || 'An error occurred. Please try again.');
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.error_message) {
                setErrorMessage(data.error_message);
            }
        } catch (e) {
            setErrorMessage('Network error. Please try again.');
            Sentry.captureException(e);
        } finally {
            setWaiting(false);
        }
    };

    const HandleRecover = async (event) => {
        event.preventDefault();
        setWaiting(true);

        try {

            const result = await fetch("/api/recover", {
                method: "POST",
                body: new FormData(event.target),
            });

            if (result.ok) {
                result.json().then((data) => {
                    if (data.redirect) {
                        setWaiting(false);
                        window.location.href = data.redirect;
                    } else if (data.error_message) {
                        setErrorMessage(data.error_message);
                        setWaiting(false);
                    } else {
                        setWaiting(false);
                    }
                });
            } else {
                const data = await result.json();
                setErrorMessage(data.error_message || "Login failed. Please try again.");
                setWaiting(false);
            }

        } catch (e) {
            setErrorMessage('Network error. Please try again.');
            setWaiting(false);
            Sentry.captureException(e);
        } finally {
            setWaiting(false);
        }
    };

    const HandleAdminRecover = async (event) => {
        event.preventDefault();
        setWaiting(true);

        try {
            const result = await fetch("/api/admin_recover", {
                method: "POST",
                body: new FormData(event.target),
            });

            const data = await result.json();

            if (!result.ok) {
                setErrorMessage(data.error_message || 'An error occurred. Please try again.');
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.error_message) {
                setErrorMessage(data.error_message);
            }
        } catch (e) {
            setErrorMessage('Network error. Please try again.');
            Sentry.captureException(e);
        } finally {
            setWaiting(false);
        }
    };


    return (
        <div className="login-page">
            <IndexHeaderNav />
            <div className="login-shell">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-header-left">
                            <button
                                className="back-btn"
                                onClick={() => navigate("/")}
                                type="button"
                            >
                                ← Back
                            </button>
                        </div>

                        <div className="login-header-center">
                            <h1>{recover ? "Recover Password" : adminLogin ? "Admin Login" : "Login"}</h1>
                        </div>

                        <div className="login-header-right">
                            {!recover && adminLogin && (
                                <img
                                    src={theme.logo}
                                    alt="Rezify Logo"
                                    className="admin-logo"
                                />
                            )}
                        </div>
                    </div>


                    {!recover && (
                      <p className="muted">
                        {adminLogin
                          ? "Sign in to access the Rezify admin dashboard."
                          : "Sign in to access your personalized job matches."}
                      </p>
                    )}


                    {recover && (
                        <p className="muted">
                            Enter your account email and we’ll send you a recovery code.
                        </p>
                    )}

                    {fromRecover && !recover && (
                        <div className="success-box">
                            ✅ Password changed successfully. Please login to proceed.
                        </div>
                    )}

                    {recover ? (
                        <form
                            method="post"
                            onSubmit={(event) => {
                                adminLogin ? HandleAdminRecover(event) : HandleRecover(event);
                            }}
                            className="login-form"
                        >
                            <div className="field">
                                <label>Email</label>
                                <div className="email-wrap">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@school.edu"
                                        aria-label="Enter your email"
                                        required
                                        maxLength={50}
                                    />
                                </div>
                            </div>

                            {errorMessage.length >= 1 && (
                                <div className="error-box">
                                    {errorMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                className="primary-btn"
                                disabled={waiting}
                            >
                                {waiting ? (
                                    <span className="btn-inline">
                                        <span className="spinner" />
                                    </span>
                                ) : (
                                    "Send Email"
                                )}
                            </button>

                            <button
                                className="link-btn"
                                type="button"
                                onClick={() => {
                                    setRecover(false);
                                    setErrorMessage("");
                                    window.location.href = "/login";
                                }}
                            >
                                Back to login
                            </button>
                        </form>
                    ) : (
                        <>
                            <form
                                method="post"
                                className="login-form"
                                onSubmit={(event) => {
                                    if (validateLoginForm(event)) {
                                        adminLogin ? HandleAdminLogIn(event) : HandleLogIn(event);
                                    }
                                }}
                            >
                                <div className="field">
                                    <label>Email</label>
                                    <div className="email-wrap">
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="you@school.edu"
                                            required
                                            maxLength={50}
                                            aria-label="Enter your email"
                                        />
                                    </div>
                                </div>

                                <div className="field">
                                    <label>Password</label>

                                    <div className="password-wrap">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            placeholder="Password"
                                            required
                                            maxLength={40}
                                            aria-label="Enter your password"
                                        />

                                        <button
                                            type="button"
                                            className="password-eye"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            onClick={() => setShowPassword((v) => !v)}
                                        >
                                            <img
                                                src={
                                                    showPassword
                                                        ? "/static/icons/eye-closed.png"
                                                        : "/static/icons/eye-open.png"
                                                }
                                                alt=""
                                                className="password-eye-icon"
                                            />
                                        </button>
                                    </div>
                                </div>


                                {errorMessage.length >= 1 && (
                                    <div className="error-box">
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="primary-btn"
                                    disabled={waiting}
                                >
                                    {waiting ? (
                                        <span className="btn-inline">
                                            <span className="spinner" />
                                        </span>
                                    ) : (
                                        "Login"
                                    )}
                                </button>

                            </form>

                            {/* OAuth Buttons (only when provider allows it) */}
                            {(showGoogleAuth || showMicrosoftAuth) && (
                                <>
                                    <div className="or-divider">
                                        <span className="or-divider-text">or</span>
                                    </div>

                                    <div
                                        className="auth-grid"
                                        data-count={(showGoogleAuth ? 1 : 0) + (showMicrosoftAuth ? 1 : 0)}
                                    >
                                        {showGoogleAuth && (
                                            <div className="auth-item">
                                                <GoogleAuth
                                                    next={adminLogin ? "/admin" : "/results"}
                                                    cameFrom={adminLogin ? "admin_login" : "login"}
                                                />
                                            </div>
                                        )}

                                        {showMicrosoftAuth && (
                                            <div className="auth-item">
                                                <MicrosoftAuth
                                                    next={adminLogin ? "/admin" : "/results"}
                                                    cameFrom={adminLogin ? "admin_login" : "login"}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="login-actions">
                                {["invalid credentials", "incorrect password"].some(msg =>
                                      errorMessage?.toLowerCase().includes(msg)
                                    ) && (
                                      <button
                                        className="link-btn"
                                        type="button"
                                        onClick={() => {
                                          setRecover(true);
                                          setErrorMessage("");
                                        }}
                                      >
                                        Forgot Password?
                                      </button>
                                    )}

                                <button
                                    className="link-btn"
                                    type="button"
                                    onClick={() => {
                                        window.location.href = adminLogin
                                            ? "/register?type=admin"
                                            : "/register";
                                    }}
                                >
                                    Register
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export { Login };
